import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import Button from '@/components/ui/Button';
import DonorDonationsReportControl from '@/components/DonorDonationsReport/DonorDonationsReportControl';
import { useGetAuthApi } from '@/hooks/UseApiCalls';

interface DonorReportSearchResult {
  contactId: number;
  firstName: string;
  lastName: string;
  israeliId: string;
  phone: string;
  city: string;
}

const SEARCH_DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 2;

const fullName = (donor: DonorReportSearchResult) => `${donor.firstName} ${donor.lastName}`.trim();

/**
 * «דוח תרומות לתורם» in management (HAT-1462): find a donor in any fund - by the start of their
 * names, or their exact Israeli id or contact id (see SearchDonorsAsync for why) - pick a year, and get the
 * report of everything they gave that year across all funds. A coordinator gets the same report,
 * for their own fund only, from a donation's details on the transactions board.
 */
const DonorDonationsReportPage = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounceValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const [selected, setSelected] = useState<DonorReportSearchResult | null>(null);

  const canSearch = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const {
    data: donors,
    isFetching,
    isError,
  } = useGetAuthApi<DonorReportSearchResult[]>(canSearch && !selected ? `management/donor-reports/donors?query=${encodeURIComponent(debouncedQuery)}` : '');

  return (
    <div className="w-full flex flex-col gap-4 max-w-[720px]">
      <div>
        <h1 className="heading-h3-semibold text-(--font-heading)">{t('donorDonationsReport.pageTitle')}</h1>
        <p className="body-md-regular text-(--font-body-1)">{t('donorDonationsReport.pageDescription')}</p>
      </div>

      {selected ? (
        <section className="flex flex-col gap-3 rounded-lg border border-(--border-main) bg-(--surface-base) p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="body-md-regular text-(--font-body-1)">
              <p className="body-md-semibold text-(--font-heading)">{fullName(selected)}</p>
              {selected.israeliId && <p>{selected.israeliId}</p>}
              {[selected.phone, selected.city].filter(Boolean).length > 0 && <p>{[selected.phone, selected.city].filter(Boolean).join(' · ')}</p>}
              <p>
                {t('donorDonationsReport.contactId')}: {selected.contactId}
              </p>
            </div>
            <Button variant="outline" onClick={() => setSelected(null)}>
              {t('donorDonationsReport.changeDonor')}
            </Button>
          </div>
          <DonorDonationsReportControl
            key={selected.contactId}
            yearsUrl={`management/donor-reports/${selected.contactId}/years`}
            reportUrlOf={(year) => `management/donor-reports/${selected.contactId}?year=${year}`}
            previewUrlOf={(year) => `management/donor-reports/${selected.contactId}/preview?year=${year}`}
            fileNameOf={(year) => `${t('donorDonationsReport.fileName')} ${fullName(selected)} ${year}`}
          />
        </section>
      ) : (
        <section className="flex flex-col gap-2">
          <SearchInput value={query} onChange={setQuery} placeholder={t('donorDonationsReport.searchPlaceholder')} />
          {!canSearch && <p className="body-sm-regular text-(--font-body-2)">{t('donorDonationsReport.searchHint')}</p>}
          {canSearch && isFetching && <p className="body-sm-regular text-(--font-body-2)">{t('donorDonationsReport.searching')}</p>}
          {canSearch && isError && (
            <p role="alert" className="body-sm-regular text-(--font-primary)">
              {t('donorDonationsReport.searchError')}
            </p>
          )}
          {canSearch && !isFetching && !isError && donors?.length === 0 && (
            <p className="body-sm-regular text-(--font-body-2)">{t('donorDonationsReport.noResults')}</p>
          )}
          {canSearch && !isFetching && donors && donors.length > 0 && (
            <ul className="flex flex-col rounded-lg border border-(--border-main) bg-(--surface-base) divide-y divide-(--border-main)">
              {donors.map((donor) => (
                <li key={donor.contactId}>
                  <button
                    type="button"
                    className="w-full text-start px-4 py-2 hover:bg-(--surface-action) cursor-pointer"
                    onClick={() => setSelected(donor)}
                  >
                    <span className="body-md-semibold text-(--font-heading)">{fullName(donor) || donor.contactId}</span>
                    <span className="body-sm-regular text-(--font-body-2)">
                      {' '}
                      {[donor.israeliId, donor.phone, donor.city].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};

export default DonorDonationsReportPage;
